<template lang="pug">
nav.table-footer(:aria-label="$vuetify.lang.t('$vuetify.pagination.ariaLabel.wrapper')")
  div.table-footer__summary
    div.table-footer__size(v-if="countItems > 5")
      span.text--secondary {{ rowsLabel }}
      v-select.table-footer__select(
        :value="pageSize"
        :items="pageSizeOptions"
        :aria-label="rowsLabel"
        :menu-props="{ offsetY: true, maxHeight: 280 }"
        outlined
        dense
        hide-details
        color="primary"
        @change="changePageSize"
      )
    span.table-footer__range.text--secondary {{ range }} з {{ countItems }} {{ $t(labelPage) }}
  div.table-footer__pagination
    v-autocomplete.table-footer__select.table-footer__page(
      :value="page"
      :items="pages"
      :aria-label="$vuetify.lang.t('$vuetify.pagination.ariaLabel.currentPage', page)"
      :menu-props="{ offsetY: true, maxHeight: 280 }"
      inputmode="numeric"
      outlined
      dense
      hide-details
      hide-no-data
      color="primary"
      @change="changePage"
    )
    span.text--secondary з {{ pageCount }} {{ $t('pages') }}
    div.table-footer__navigation
      v-btn.table-footer__button(
        icon
        outlined
        color="primary"
        width="36"
        height="36"
        :disabled="page <= 1"
        :aria-label="$vuetify.lang.t('$vuetify.dataFooter.prevPage')"
        :title="$vuetify.lang.t('$vuetify.dataFooter.prevPage')"
        @click="changePage(page - 1)"
      )
        v-icon(size="20") mdi-chevron-left
      v-btn.table-footer__button(
        icon
        outlined
        color="primary"
        width="36"
        height="36"
        :disabled="page >= pageCount"
        :aria-label="$vuetify.lang.t('$vuetify.dataFooter.nextPage')"
        :title="$vuetify.lang.t('$vuetify.dataFooter.nextPage')"
        @click="changePage(page + 1)"
      )
        v-icon(size="20") mdi-chevron-right
</template>

<script>
import { capitalizeFirstLetter } from '@/configs/helpers'

export default {
  name: 'TableFooter',
  props: {
    page: { type: Number, required: true },
    pageSize: { type: Number, required: true },
    countItems: { type: Number, required: true },
    labelPage: { type: String, default: 'record' },
    pageSizeOptions: { type: Array, default: () => [5, 10, 20, 50] }
  },
  computed: {
    rowsLabel () {
      return `${capitalizeFirstLetter(this.$t(this.labelPage))} ${this.$t('on')} ${this.$t('page')}`
    },
    pageCount () { return Math.max(1, Math.ceil(this.countItems / this.pageSize)) },
    pages () { return Array.from({ length: this.pageCount }, (_, index) => index + 1) },
    range () {
      const first = Math.min(this.countItems, ((this.page - 1) * this.pageSize) + 1)
      const last = Math.min(this.countItems, this.page * this.pageSize)
      return `${first} - ${last}`
    }
  },
  methods: {
    changePage (value) {
      if (Number.isInteger(value) && value >= 1 && value <= this.pageCount && value !== this.page) {
        this.$emit('page-change', value)
      }
    },
    changePageSize (value) {
      if (this.pageSizeOptions.includes(value) && value !== this.pageSize) this.$emit('page-size-change', value)
    }
  }
}
</script>

<style lang="sass">
@import '@/assets/sass/constants.sass'

$support-launcher-clearance: 44px

.table-footer
  display: flex
  flex-wrap: wrap
  align-items: center
  justify-content: space-between
  gap: 12px 24px
  min-height: 60px
  // Balance both sides while leaving room for the floating support launcher.
  padding: 12px (16px + $support-launcher-clearance)
  border-top: 1px solid $light-gray200
  border-radius: 0 0 8px 8px
  background-color: $light-gray
  font-size: 14px

  &__summary, &__pagination, &__size, &__navigation
    display: flex
    align-items: center
    gap: 8px

  &__summary
    flex-wrap: wrap
    gap: 8px 20px
    min-width: 0

  &__size, &__range, &__pagination
    white-space: nowrap

  &__pagination
    flex-wrap: wrap
    margin-left: auto

  &__select.v-input
    flex: 0 0 84px
    width: 84px
    max-width: 84px
    font-size: 14px

    > .v-input__control > .v-input__slot
      min-height: 36px !important
      margin: 0
      background-color: white

      fieldset
        border-radius: 4px !important

    .v-select__selections
      min-height: 34px
      padding: 0 !important

    .v-input__append-inner
      align-self: center
      margin-top: 0 !important

    .v-select__selection--comma
      margin-top: 0
      margin-bottom: 0

  &__page.v-input
    flex-basis: 80px
    width: 80px
    max-width: 80px

    &.v-autocomplete.v-select input
      min-width: 0

  &__button.v-btn
    padding: 0 !important

@media (max-width: 600px)
  .table-footer
    padding: 12px (12px + $support-launcher-clearance)

    &__summary, &__size, &__pagination
      min-width: 0
      max-width: 100%

    &__size, &__navigation
      flex-wrap: wrap

    &__size, &__range, &__pagination
      white-space: normal

    &__pagination
      margin-left: 0

    &__select.v-input
      flex-shrink: 1
      min-width: 0
      max-width: 100%
</style>
